import type { CancelToken } from 'axios';
import { createApiClient } from '@/api/client';
import type { CopyrightMusicDTO } from '@/types/copyright-music';
import type { EventDTO, EventVideoDTO } from '@/types/event';

export type DownloadVariant = 'original' | 'muted';

export interface CreateChunkedMediaResponse {
    id: number;
    sas?: string;
    url: string;
    order: number;
}

export interface CreateExternalMediaPayload {
    fileName: string;
    order: number;
    url: string;
}

export interface EventVodDTO {
    playbackUrl: string;
}

export interface LiveStreamDTO {
    cloudStreamingProvider?: number | null;
    facebookSimulcast?: boolean | null;
}

export interface AdminEventEditDTO {
    id: number;
    externalId?: string | null;
    funeralHomeId?: number | null;
    serviceId?: number | null;
    ownerId?: number | null;
    title?: string | null;
    duration?: number | string | null;
    oldServiceId?: number | null;
    pin?: string | null;
    order?: number | null;
    eventStatus?: number | null;
    eventType?: number | null;
    time?: string | null;
    createDate?: string | null;
    convertedVideo?: string | null;
    liveURL?: string | null;
    liveStreamId?: number | null;
    liveSecondsConsumed?: number | null;
    previewText?: string | null;
    videoThumbnail?: string | null;
    passareEventUuid?: string | null;
    videoQueType?: number | null;
    source?: string | null;
    emailNotified?: boolean | string | null;
    hasCopyrightMusic?: boolean | string | null;
    voDs?: string | null;
    userId?: number | null;
    issue?: number | null;
    problemAtStreamBadHealth?: boolean | null;
    problemAtStreamNotStarted?: boolean | null;
    muted?: boolean | null;
    private?: boolean | null;
    live?: boolean | null;
    introVideo?: boolean | null;
    hidden?: boolean | null;
    notes?: string | null;
    [key: string]: unknown;
}

export interface CreateCopyrightMusicPayload {
    song: string;
    artist: string;
    timeStamp: string;
    endTimeStamp: string;
    mutedInVideo: boolean;
}

export interface UpdateCopyrightMusicPayload {
    song: string;
    artist: string;
    timeStamp: string;
    endTimeStamp: string;
    mutedInVideo: boolean;
}

export interface UpdateCopyrightMusicMutedInVideoPayload {
    mutedInVideo: boolean;
}

export interface SaveCopyrightMusicItemPayload {
    id?: number;
    song: string;
    artist: string;
    timeStamp: string;
    endTimeStamp: string | null;
    mutedInVideo: boolean;
}

export interface SaveCopyrightMusicPayload {
    copyrightMusic: SaveCopyrightMusicItemPayload[];
}

export interface SaveCopyrightMusicResponse {
    results?: CopyrightMusicDTO[];
    Results?: CopyrightMusicDTO[];
    total?: number;
    Total?: number;
    renderQueued?: boolean;
    RenderQueued?: boolean;
    message?: string | null;
    Message?: string | null;
}

export type UpdateAdminEventEditPayload = Record<string, unknown>;

export async function getCopyrightMusic(eventId: number): Promise<CopyrightMusicDTO[]> {
    const api = createApiClient();
    const response = await api.get(`/Events/copyright-music/${eventId}`);
    const results = (response?.data as { results?: unknown })?.results;

    return Array.isArray(results) ? (results as CopyrightMusicDTO[]) : [];
}

export async function createCopyrightMusic(
    eventId: number,
    payload: CreateCopyrightMusicPayload,
): Promise<CopyrightMusicDTO> {
    const api = createApiClient();
    const response = await api.post(`/Events/copyright-music/${eventId}`, payload);

    return response.data as CopyrightMusicDTO;
}

export async function updateCopyrightMusic(
    eventId: number,
    musicId: number,
    payload: UpdateCopyrightMusicPayload,
): Promise<CopyrightMusicDTO> {
    const api = createApiClient();
    const response = await api.put(`/Events/copyright-music/${eventId}/${musicId}`, payload);

    return response.data as CopyrightMusicDTO;
}

export async function updateCopyrightMusicMutedInVideo(
    eventId: number,
    musicId: number,
    payload: UpdateCopyrightMusicMutedInVideoPayload,
): Promise<CopyrightMusicDTO> {
    const api = createApiClient();
    const response = await api.patch(`/Events/copyright-music/${eventId}/${musicId}/muted-in-video`, payload);

    return response.data as CopyrightMusicDTO;
}

export async function deleteCopyrightMusic(eventId: number, musicId: number): Promise<void> {
    const api = createApiClient();
    await api.delete(`/Events/copyright-music/${eventId}/${musicId}`);
}

export async function saveCopyrightMusic(
    eventId: number,
    payload: SaveCopyrightMusicPayload,
): Promise<SaveCopyrightMusicResponse> {
    const api = createApiClient();
    const response = await api.put(`/Events/copyright-music/${eventId}/save-and-rerender`, payload);

    return response.data as SaveCopyrightMusicResponse;
}

export async function getEvent(eventId: number | string): Promise<EventDTO> {
    const api = createApiClient();
    const response = await api.get(`/Events/${eventId}`);

    return response.data as EventDTO;
}

export async function getAdminEventEdit(eventId: number | string): Promise<AdminEventEditDTO> {
    const api = createApiClient();
    const response = await api.get(`/Events/admin-edit/${eventId}`);

    return response.data as AdminEventEditDTO;
}

export async function updateAdminEventEdit(
    eventId: number | string,
    payload: UpdateAdminEventEditPayload,
): Promise<void> {
    const api = createApiClient();
    await api.put(`/Events/admin-edit/${eventId}`, payload);
}

export async function getEventsByServiceId(serviceId: number): Promise<EventDTO[]> {
    const api = createApiClient();
    const response = await api.get(`/events/list/${serviceId}`);
    const results = response?.data;

    return Array.isArray(results) ? (results as EventDTO[]) : [];
}

export async function getEventVideo(eventId: number | string): Promise<EventVideoDTO> {
    const api = createApiClient();
    const response = await api.get(`/Videos/events/${eventId}`);

    return response.data as EventVideoDTO;
}

export async function getLiveStream(liveStreamId: number | string): Promise<LiveStreamDTO> {
    const api = createApiClient();
    const response = await api.get(`/live/${liveStreamId}`);

    return response.data as LiveStreamDTO;
}

export async function stopLiveStream(liveStreamId: number | string): Promise<void> {
    const api = createApiClient();
    await api.post(`/live/stop/${liveStreamId}`);
}

export async function get(eventId: number | string): Promise<EventDTO> {
    return getEvent(eventId);
}

export async function getAdmin(eventId: number | string): Promise<AdminEventEditDTO> {
    return getAdminEventEdit(eventId);
}

export async function updateAdmin(eventId: number | string, payload: UpdateAdminEventEditPayload): Promise<void> {
    await updateAdminEventEdit(eventId, payload);
}

export async function createIsoRenderJob(eventId: number | string): Promise<void> {
    const api = createApiClient();
    await api.post(`/render/create-iso/${eventId}`);
}

export async function getIsoDownloadSasUrl(eventId: number | string): Promise<string | undefined> {
    const api = createApiClient();
    const response = await api.post(`/Events/download-iso/${eventId}`, { responseType: 'blob' });

    return (response?.data as { sas?: string })?.sas;
}

export async function getEventDownloadSasUrl(
    eventId: number | string,
    variant: DownloadVariant,
    cancelToken?: CancelToken,
): Promise<string | undefined> {
    const api = createApiClient();
    const path = variant === 'muted' ? `/Events/download/muted/${eventId}` : `/Events/download/${eventId}`;
    const response = await api.post(path, undefined, { cancelToken });

    return (response?.data as { sas?: string })?.sas;
}

export async function getVods(eventId: number | string): Promise<EventVodDTO[]> {
    const api = createApiClient();
    const response = await api.get(`/live/get-vod/${eventId}`);
    const results = response?.data;

    return Array.isArray(results) ? (results as EventVodDTO[]) : [];
}

export async function createChunkedMedia(
    eventId: number | string,
    fileName: string,
    order: number,
): Promise<CreateChunkedMediaResponse> {
    const api = createApiClient();
    const response = await api.post(
        `/events/createmedia/chunked/${eventId}?fileName=${encodeURIComponent(fileName)}&order=${order}`,
    );

    return response.data as CreateChunkedMediaResponse;
}

export async function createExternalMedia(
    eventId: number | string,
    payload: CreateExternalMediaPayload,
): Promise<CreateChunkedMediaResponse> {
    const api = createApiClient();
    const response = await api.post(`/events/createmedia/external/${eventId}`, payload);

    return response.data as CreateChunkedMediaResponse;
}

export async function deleteIso(eventId: number | string): Promise<void> {
    const api = createApiClient();
    await api.delete(`/Events/delete-iso/${eventId}`);
}

export async function createApplyMutedAudioTrackRenderJob(eventId: number | string): Promise<void> {
    const api = createApiClient();
    await api.post(`/render/create-apply-muted-audio-track/${eventId}`);
}

export default function EventsService() {
    return {
        get,
        getAdmin,
        updateAdmin,
        getCopyrightMusic,
        createCopyrightMusic,
        updateCopyrightMusic,
        updateCopyrightMusicMutedInVideo,
        deleteCopyrightMusic,
        saveCopyrightMusic,
        getEvent,
        getAdminEventEdit,
        updateAdminEventEdit,
        getEventsByServiceId,
        getEventVideo,
        getLiveStream,
        stopLiveStream,
        deleteIso,
        createApplyMutedAudioTrackRenderJob,
        createIsoRenderJob,
        getIsoDownloadSasUrl,
        getEventDownloadSasUrl,
        getVods,
        createChunkedMedia,
        createExternalMedia,
    };
}
